// =======================================
// File: src/api/axios.js
// =======================================
import axios from "axios";

const API_BASE = "https://landlord-portal-y3wv.onrender.com";
const LANDLORD_PREFIX = "/api/v1/landlord/";
const LANDLORD_SIGNIN_PATH = "/landlord-signin";
const REFRESH_URL = "/api/v1/auth/landlord/refresh-token";

const api = axios.create({ baseURL: API_BASE });

let _redirecting = false;
let _refreshPromise = null;

// ---------- storage helpers ----------
const getAny = (k) => localStorage.getItem(k) || sessionStorage.getItem(k);
function getToken() {
  return getAny("access_token");
}
function getTokenType() {
  return getAny("token_type") || "Bearer";
}

function getRefreshToken() {
  return localStorage.getItem("refresh_token");
}

function storeTokens(accessToken, tokenType, refreshToken) {
  const remember = localStorage.getItem("remember_me") === "true";
  const store = remember ? localStorage : sessionStorage;
  if (accessToken) store.setItem("access_token", accessToken);
  if (tokenType) store.setItem("token_type", tokenType);
  // ✅ only persist RT when remember = true (localStorage)
  if (remember) {
    if (refreshToken) localStorage.setItem("refresh_token", refreshToken);
  } else {
    // ensure no RT is kept for session-only logins
    localStorage.removeItem("refresh_token");
    sessionStorage.removeItem("refresh_token");
  }
}
function hardLogoutAndRedirect(
  reason = "session_expired",
  details = "Your session has ended. Please sign in again."
) {
  if (_redirecting) return;
  _redirecting = true;

  try {
    sessionStorage.setItem(
      "post_auth_notice",
      JSON.stringify({ type: reason, message: details, ts: Date.now() })
    );
    const KEYS = [
      "access_token",
      "token_type",
      "user",
      "user_email",
      "refresh_token",
      "remember_me",
    ];
    [localStorage, sessionStorage].forEach((s) =>
      KEYS.forEach((k) => s.removeItem(k))
    );
  } catch {}

  if (typeof window !== "undefined")
    window.location.replace(LANDLORD_SIGNIN_PATH);
}

function isJwtExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload?.exp === "number" && Date.now() >= payload.exp * 1000;
  } catch {
    return false;
  }
}

// ---------- refresh de-duped ----------
async function ensureFreshAccessToken() {
  const access = getToken();
  const refresh = getRefreshToken();

  // nothing we can do
  if (!refresh && !access) return null;

  // need refresh if missing or expired
  const needRefresh = !access || isJwtExpired(access);
  if (!needRefresh) return access;

  if (!_refreshPromise) {
    _refreshPromise = axios
      .post(`${API_BASE}${REFRESH_URL}`, { refresh_token: refresh })
      .then((res) => {
        const {
          access_token,
          token_type = "Bearer",
          refresh_token: newRefresh,
        } = res?.data || {};
        if (!access_token) throw new Error("No new access token from refresh");
        storeTokens(access_token, token_type, newRefresh || refresh);
        return access_token;
      })
      .catch((err) => {
        // bubble up; caller will decide to logout
        throw err;
      })
      .finally(() => {
        _refreshPromise = null;
      });
  }

  return _refreshPromise;
}

// ---------- REQUEST interceptor ----------
// 1) If token missing/expired but refresh exists → refresh BEFORE sending
api.interceptors.request.use(
  async (config) => {
    const access = getToken();
    const refresh = getRefreshToken();

    if ((!access && refresh) || (access && isJwtExpired(access) && refresh)) {
      try {
        const newAccess = await ensureFreshAccessToken();
        if (newAccess) {
          config.headers = {
            ...(config.headers || {}),
            Authorization: `${getTokenType()} ${newAccess}`,
          };
          return config;
        }
      } catch {}
    }

    const tokenNow = getToken();
    if (tokenNow) {
      config.headers = {
        ...(config.headers || {}),
        Authorization: `${getTokenType()} ${tokenNow}`,
      };
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---------- RESPONSE interceptor ----------
api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config || {};
    const status = error?.response?.status;
    const url = (() => {
      try {
        return new URL(original?.url || "", API_BASE).href;
      } catch {
        return `${API_BASE}${original?.url || ""}`;
      }
    })();

    const isLandlordCall =
      typeof url === "string" && url.includes(LANDLORD_PREFIX);
    const shouldTry =
      isLandlordCall &&
      !original._retry &&
      (status === 401 || status === 403) &&
      !!getRefreshToken();

    if (shouldTry) {
      try {
        const newAccess = await ensureFreshAccessToken();
        if (newAccess) {
          original._retry = true;
          original.headers = {
            ...(original.headers || {}),
            Authorization: `${getTokenType()} ${newAccess}`,
          };
          return api(original);
        }
      } catch {}
    }

    if (
      isLandlordCall &&
      (status === 401 || status === 403 || status === 419 || status === 440)
    ) {
      hardLogoutAndRedirect(
        "session_expired",
        "Your session expired. Please sign in again."
      );
    }

    return Promise.reject(error);
  }
);

export const loginLandlord = (data) =>
  api.post("/api/v1/auth/landlord/login", data);
export const sendOtpLandlord = (data, token) =>
  api.post("/api/v1/otp/landlord/send-otp", data, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
export const verifyOtp = (data, token) =>
  api.post("/api/v1/otp/verify-otp", data, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

export default api;
